import React, { useCallback } from 'react';
import { NavLink, Redirect, Route, Switch } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { PiChatTextLight, PiPlusLight, PiSignOutLight, PiUserCircleLight } from 'react-icons/pi';
import clsx from 'clsx';
import Logo from '../shared/brand/Logo';
import Button from '../shared/TailwindComponents/Button';
import MarketplaceRequestsPage from '../../pages/MarketplaceRequestsPage';
import MarketplaceNewRequestPage from '../../pages/MarketplaceNewRequestPage';
import MarketplaceRequestDetailPage from '../../pages/MarketplaceRequestDetailPage';
import MarketplaceVerificationCallbackPage from '../../pages/MarketplaceVerificationCallbackPage';
import ClientAccountPage from '../../pages/ClientAccountPage';
import { Account, CLIENT_SIGNUP_PATH } from '../../services/api/accountService';
import EmailConfirmBanner from './EmailConfirmBanner';
import { useAppDispatch } from '../../store';
import { clearToken } from '../../store/actions/SignInAction';
import apis from '../../utils/apis';

/**
 * The whole app for a CLIENT account: their own requests, a new request, and
 * their account. No sidebar, no menu, no workspace routes - the backend
 * refuses those for a client anyway (core/auth/account_types.py); this shell
 * just never offers them.
 *
 * URLs are the marketplace's own, so links in emails and notifications work
 * the same for a client as anywhere else.
 */

export const useSignOut = () => {
  const { logout } = useAuth0();
  const dispatch = useAppDispatch();
  return useCallback(async () => {
    try {
      await apis.post('/devices/logout-all');
    } catch {
      // Signing out locally still works.
    }
    localStorage.clear();
    sessionStorage.clear();
    dispatch(clearToken());
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [dispatch, logout]);
};

const NAV = [
  { to: '/marketplace', label: 'My cases', icon: PiChatTextLight, exact: true },
  { to: '/marketplace/new', label: 'New request', icon: PiPlusLight, exact: true },
  { to: '/account', label: 'Account', icon: PiUserCircleLight, exact: true },
];

interface ClientShellProps {
  account: Account;
  onAccountChange: (account: Account) => void;
}

const ClientShell: React.FC<ClientShellProps> = ({ account, onAccountChange }) => {
  const { t } = useTranslation();
  const signOut = useSignOut();

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="glass-bar sticky top-0 z-40">
        <div className="mx-auto flex w-full max-w-5xl items-center gap-4 px-4 py-3">
          <Logo variant="wordmark" className="h-6 w-auto flex-shrink-0" />
          <nav aria-label={t('Main')} className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
            {NAV.map(({ to, label, icon: Icon, exact }) => (
              <NavLink
                key={to}
                to={to}
                exact={exact}
                className="nav-pill flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-fg-muted"
                activeClassName="nav-pill-active !text-fg-strong"
              >
                <Icon className="h-4 w-4" />
                <span>{t(label)}</span>
              </NavLink>
            ))}
          </nav>
          <Button variant="tertiary" size="small" onClick={signOut} aria-label={t('Sign out')}>
            <PiSignOutLight className="h-4 w-4" />
            <span className="hidden sm:inline">{t('Sign out')}</span>
          </Button>
        </div>
      </header>

      <main className={clsx('mx-auto w-full max-w-5xl flex-1 px-4 py-6 space-y-6')}>
        {!account.email_verified && <EmailConfirmBanner onConfirmed={onAccountChange} />}
        <Switch>
          <Route exact path="/marketplace" component={MarketplaceRequestsPage} />
          <Route exact path="/marketplace/new" component={MarketplaceNewRequestPage} />
          <Route exact path="/marketplace/requests/:leadUuid" component={MarketplaceRequestDetailPage} />
          <Route exact path="/marketplace/verification/callback" component={MarketplaceVerificationCallbackPage} />
          <Route exact path="/account">
            <ClientAccountPage account={account} onAccountChange={onAccountChange} onSignOut={signOut} />
          </Route>
          <Route exact path={[CLIENT_SIGNUP_PATH, '/marketplace/intake']}>
            <Redirect to="/marketplace/new" />
          </Route>
          <Route path="*">
            <Redirect to="/marketplace" />
          </Route>
        </Switch>
      </main>
    </div>
  );
};

export default ClientShell;
